import { defineStore } from 'pinia'
import type Player from '@/types/Player'
import type PlayerShipDesign from '@/types/PlayerShipDesign.ts'
import type PlayerMinister from '@/types/PlayerMinister.ts'
import type PlayerTechQueue from '@/types/TechQueue'
import Backend from '@/models/backend'
import { logger } from '@/models/logger';
export const useGameStore = defineStore('gameStore', {
  state: () => ({
    galaxyTileGridSize: 0 as number,
    sectorTileGridSize: 0 as number,
    dayInterval: 0 as number,
    weekInterval: 0 as number,
    ringworldOrePerPlanet: 0 as number,
    combatMode: '' as string,
    gameFinished: false as boolean,
    startsIn: 0 as number,
    startTime: 0 as number,
    websocketInitialSyncCompleted: false as boolean,
    gameOutOfSync: false as boolean,
    backendError: '' as string,
    playerToken: '' as string,
    authPlayerId: 0 as number,
    turn: 0 as number,
    shipCommandBlock: false as boolean, // if ship commands are blocked
    dayTurnedAt: 0 as number,
    weekTurnedAt: 0 as number,
    mapInitialized: false as boolean,
    player: null as Player | null,
    playerShipDesigns: [] as Record<number, PlayerShipDesign>,
    playerProductionObjects: new Map<number, string>(),
    playerShipComponents: new Map<number, string>(),
    playerMinisters: new Map<string, PlayerMinister>(),
    playerTechs: new Map<number, number>(),
    playerPrototypes: new Map<number, number>(),
    playerBuildingSettings: new Map<string, number>(),
    cdn: import.meta.env.VITE_OLD_CDN_URL as string,// Deprecated
    cdnUrl: import.meta.env.VITE_CDN_URL as string,
    modalType: '' as string, //Pops up a model with the given type
    modalData: {} as any, //Data to be passed to the modal
    modal2Type: '' as string, //Pops up a model with the given type (second modal to layer on top of the first)
    modal2Data: {} as any, //Data to be passed to the modal (second modal to layer on top of the first)
    infoModalType: '' as string, //Info modals (welcome, tutorials, etc)
    pendingRequestId: '' as string,
    pendingRequestPopupVisible: false as boolean,
    tinyToastMessage: '' as string,
    tinyToastType: '' as string,
    pullDownMenuVisible: '' as string, //Used to show/hide multiple pull down menus, so they don't overlap
    loadingText: '' as string,
    routerLastUrl: '' as string, // Last URL visited by the router, for back button
    playerTechQueue: {} as PlayerTechQueue,
    alliancePrototypeSelection: false as boolean, // Flag for alliance prototype selection mode
    alliancePrototypeReturnRoute: '' as string, // Route to return to after prototype selection
    leaderboard: null as any, // Leaderboard data from websocket
    hoursOnline: 0 as number, // Hours online in the last 24 hours
    hoursOnlineLimit: 0 as number, // Maximum hours online limit
    activeTimestamps: [] as number[] // Active hour timestamps
  }),
  getters: {
    getShipDesignById:
      (state) =>
      (id: number): PlayerShipDesign | undefined => {
        return Object.values(state.playerShipDesigns).find((c) => c.id === id)
      }
  },
  actions: {
    setPlayerTechQueue(queue: PlayerTechQueue) {
      this.playerTechQueue = queue
    },
    storePlayerTechQueue(queue: PlayerTechQueue) {
      this.playerTechQueue = queue
      const backend = new Backend()
      backend.storePlayerTechQueue(queue)
    },
    setTinySuccessToastMessage(message: string) {
      this.tinyToastMessage = message
      this.tinyToastType = 'success'
    },
    setTinyErrorToastMessage(message: string) {
      this.tinyToastMessage = message
      this.tinyToastType = 'error'
    },
    setPendingRequest(): string {
      this.pendingRequestPopupVisible = false
      this.pendingRequestId = this.generateRequestId()
      // Make the popup visible after 500ms
      setTimeout(() => {
        this.pendingRequestPopupVisible = true
      }, 700)

      return this.pendingRequestId
    },
    clearPendingRequest(requestId: string): void {
      logger.debug('Received request ID', requestId)
      if (requestId == this.pendingRequestId) {
        this.pendingRequestId = ''
      }
    },
    generateRequestId(): string {
      return '1e7-1e3-4e3-8e3-1e11'.replace(/[018]/g, (c) =>
        (
          parseInt(c) ^
          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (parseInt(c) / 4)))
        ).toString(16)
      )
    },
    hasPlayerTech(techId: number): boolean {
      return this.playerTechs.has(techId)
    },
    setAlliancePrototypeSelection(enabled: boolean, returnRoute?: string) {
      this.alliancePrototypeSelection = enabled
      if (returnRoute) {
        this.alliancePrototypeReturnRoute = returnRoute
      }
    },
    resetAlliancePrototypeSelection() {
      this.alliancePrototypeSelection = false
      this.alliancePrototypeReturnRoute = ''
    },
    setPlayerPrototype(data: { prototype_id: number } | number) {
      const techId = typeof data === 'object' ? data.prototype_id : data
      this.playerPrototypes.set(techId, techId)
    },
    removePlayerPrototype(techId: number) {
      this.playerPrototypes.delete(techId)
    },
    showModal(type: string, data: any = {}) {
      this.modalType = type
      this.modalData = data
    },
    hideModel() {
      this.modalType = ''
      this.modalData = []
    },
    canBeAttacked(targetPlayerId: number): boolean {
      // Cannot attack yourself
      if (targetPlayerId === this.player?.id) {
        return false
      }

      // Check if leaderboard data exists
      if (!this.leaderboard || !Array.isArray(this.leaderboard)) {
        return false
      }

      // Find current player and target player in leaderboard
      const currentPlayerEntry = this.leaderboard.find((entry: any) => entry.player_id === this.player?.id)
      const targetPlayerEntry = this.leaderboard.find((entry: any) => entry.player_id === targetPlayerId)

      // If either player is not in leaderboard, cannot attack
      if (!currentPlayerEntry || !targetPlayerEntry) {
        return false
      }

      // Check for canAttackOverride field - if explicitly set to 1, player can be attacked
      if (targetPlayerEntry.canAttackOverride === 1) {
        return true
      }

      // Case 1: Target player is in top 5
      if (targetPlayerEntry.position <= 5) {
        return true
      }

      // Case 2: Target player is higher than current player in leaderboard (lower position number)
      if (targetPlayerEntry.position < currentPlayerEntry.position) {
        return true
      }

      // Case 3: Target player is inactive
      if (targetPlayerEntry.inactive === 1) {
        return true
      }

      // Case 4: Target player is less than 3 places below current player
      const positionDifference = targetPlayerEntry.position - currentPlayerEntry.position
      if (positionDifference > 0 && positionDifference <= 2) {
        return true
      }

      return false
    }
  }
})
